/**
 * VisitScheduleModal – 訪問入力（新システム）
 * Layout and labels match old system: 訪問日時, 受診者名+検索, 受診者, 受診者住所, 所要時間,
 * 診療区分, コピー対象, 訪問実績から, 施術メモ. Four buttons: 閉じる, 削除, コピー, 適用.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import dayjs from 'dayjs';
import { AppModal, AppButton } from '@/components/atoms';
import type { VisitScheduleEvent, VisitDraftPayload } from '@/pages/main/visitSchedule/types';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { fetchPatientEdit, searchPatients } from '@/pages/main/patient/services';
import { useQuery } from '@tanstack/react-query';
import type { PatientFormData } from '@/pages/main/patient/types/patientTypes';
import { getPatientAddressListWithDays } from '@/services/main/patientAddresses';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useTheme } from '@/shared/providers/theme';
import useAppModal from '@/hooks/useAppModal';
import { mbConvertKana } from '@/utils/japaneseText';
import { NO_PRINT_DATA_MESSAGE } from '@/pages/main/treatmentRecord/constants';
import {
  CONSULTATION_TYPE,
  DEFAULT_VISIT_HOUR,
  DEFAULT_VISIT_MINUTES,
  DISTANCE_WARNING_MSG,
} from './VisitScheduleModal.constants';
import {
  formatPatientAddressLabel,
  getAddressLabel,
  getPatientBaseAddressFields,
  getPatientNameOnly,
  normalizeAddressLabelToOptions,
  pickActivePatientInformation,
  pickSearchedPatient,
  resolveInitialPatientAddressLabel,
  shouldShowVisitDeleteButton,
} from './VisitScheduleModal.helpers';
import { VisitScheduleModalForm } from './VisitScheduleModalForm';
import { useVisitScheduleModalActions } from './useVisitScheduleModalActions';

export interface VisitScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  visit?: VisitScheduleEvent | null;
  initialDate?: string;
  /** Time from calendar cell (HH:mm). Used when creating new from cell click. */
  initialTime?: string;
  onSave: (payload: VisitDraftPayload) => void;
  onDelete?: (id: string | number) => void;
  onCopyToOtherDays?: (payload: VisitDraftPayload, dates: string[]) => void;
  isSaving?: boolean;
  /** All events currently on calendar — used for duplicate/lock checks. */
  existingEvents?: Array<{
    id: string | number;
    patient_id: number;
    massager_id: number;
    visit_datetime_from: string;
    is_locked?: boolean;
    is_suddenly_visit?: boolean;
  }>;
  /** DB visit ids removed locally but not yet submitted. */
  pendingDeletedVisitIds?: number[];
  /** Current massager selected on the calendar page. */
  currentMassagerId?: number | null;
  /** Clinic has check-in feature enabled (controls 訪問実績 display). */
  checkinEnabled?: boolean;
  /**
   * CLM rows for the dropdown — used to resolve serial_number in 突発的な往療 duplicate error,
   * and to validate visit_date within the row's 適用期限 (available_from / available_to).
   * `id` here is the CLM id; `massager.id` is the real massagers.id (matched against visits.massager_id).
   */
  massagers?: Array<{
    id: number;
    available_from?: string | null;
    available_to?: string | null;
    massager: {
      id: number;
      serial_number: number | null;
      name: string;
    } | null;
  }>;
}

const VisitScheduleModal: React.FC<VisitScheduleModalProps> = ({
  isOpen,
  onClose,
  visit,
  initialDate,
  initialTime,
  onSave,
  onDelete,
  onCopyToOtherDays,
  isSaving = false,
  existingEvents = [],
  pendingDeletedVisitIds = [],
  currentMassagerId = null,
  checkinEnabled = false,
  massagers = [],
}) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const { error } = useAppModal();
  const isExisting = !!visit && typeof visit.id !== 'string';
  const canDeleteVisit = shouldShowVisitDeleteButton(visit);
  const isCopiedVisit = !!visit?.is_copied;

  const [visitDate, setVisitDate] = useState(dayjs());
  const [visitHour, setVisitHour] = useState<string>(String(DEFAULT_VISIT_HOUR).padStart(2, '0'));
  const [visitMinute, setVisitMinute] = useState<string>('00');
  const isVisitTimeValid = visitHour !== '' && visitMinute !== '';
  const visitTimeHHMM = isVisitTimeValid ? `${visitHour}:${visitMinute}` : '';
  const [patientSearchKeyword, setPatientSearchKeyword] = useState('');
  /** Keyword submitted via 検索 — only this value triggers API call, avoids calling when user just types */
  const [searchedKeyword, setSearchedKeyword] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0);
  /** visitDate snapshot at the moment 検索 is clicked — keeps search result stable when user changes date afterward. */
  const [searchVisitDate, setSearchVisitDate] = useState('');
  const [patientList, setPatientList] = useState<PatientFormData[]>([]);
  const [patientId, setPatientId] = useState<number | undefined>(undefined);
  const [patientAddressName, setPatientAddressName] = useState('');
  const [visitMinutes, setVisitMinutes] = useState<number | null>(DEFAULT_VISIT_MINUTES);
  const [consultationType, setConsultationType] = useState(CONSULTATION_TYPE);
  const [copyTargetWeekdays, setCopyTargetWeekdays] = useState<number[]>([]);
  const [isElectric, setIsElectric] = useState(true);
  const [treatmentMemo, setTreatmentMemo] = useState('');
  /** 突発的な往療 (RA2: 202406法改正) */
  const [isSuddenlyVisit, setIsSuddenlyVisit] = useState(false);
  const [hospitalName, setHospitalName] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [visitReason, setVisitReason] = useState('');
  const [alignmentDate, setAlignmentDate] = useState('');
  /** True when 検索 returned 0 results — force-clear 受診者/住所 and hide 突発的な往療 until next valid search/select. */
  const [searchNoResult, setSearchNoResult] = useState(false);
  /** Lifts the edit-mode "keep saved address" guard once the user has actively re-picked a 受診者. */
  const hasChangedPatientRef = useRef(false);
  const hasNormalizedSavedAddressRef = useRef(false);
  const patientSearchInputRef = useRef<InputRef>(null);

  type FormSnapshot = {
    visitDate: string;
    visitTime: string;
    patientSearchKeyword: string;
    patientId: number | undefined;
    patientAddressName: string;
    visitMinutes: number | null;
    consultationType: string;
    isElectric: boolean;
    treatmentMemo: string;
    isSuddenlyVisit: boolean;
    hospitalName: string;
    doctorName: string;
    visitReason: string;
    alignmentDate: string;
  };
  const [initialFormData, setInitialFormData] = useState<FormSnapshot | null>(null);

  // RA2 parity: snapshot visitDate at the moment 受診者 is selected. The address validity window
  // (start/end) must stay stable as the user changes visitDate — otherwise the range re-computes
  // against the new date and the もう一度受診者名を検索してください dialog can never fire.
  const [addressSearchVisitDate, setAddressSearchVisitDate] = useState<string>('');
  useEffect(() => {
    if (patientId) {
      setAddressSearchVisitDate(visitDate.format('YYYY-MM-DD'));
    } else {
      setAddressSearchVisitDate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const { data: addressResult } = useQuery({
    queryKey: [
      'visitSchedule',
      'patientAddresses',
      tenant,
      clinicNumber,
      patientId,
      addressSearchVisitDate,
    ],
    queryFn: () =>
      getPatientAddressListWithDays(tenant, clinicNumber, patientId!, addressSearchVisitDate),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!addressSearchVisitDate,
    staleTime: 60_000,
  });
  const addressList = addressResult?.data ?? [];

  // Only call API when clicking 検索: queryKey/queryFn uses searchedKeyword (set on button click), not patientSearchKeyword
  const { data: searchResult, isFetching: isSearching } = useQuery({
    queryKey: [
      'visitSchedule',
      'patientSearch',
      tenant,
      clinicNumber,
      searchedKeyword,
      searchTrigger,
      searchVisitDate,
    ],
    queryFn: () =>
      searchPatients(
        tenant,
        clinicNumber,
        searchedKeyword,
        false,
        1,
        20,
        'name_kana',
        'asc',
        searchVisitDate,
        serviceType,
        true
      ),
    enabled:
      !!tenant &&
      !!clinicNumber &&
      searchTrigger > 0 &&
      searchedKeyword.length >= 1 &&
      !!searchVisitDate,
    staleTime: 60_000,
  });

  const copiedVisitDateStr = useMemo(() => {
    if (isCopiedVisit && visit?.visit_datetime_from) {
      const copiedDate = dayjs(visit.visit_datetime_from);
      return copiedDate.isValid() ? copiedDate.format('YYYY-MM-DD') : '';
    }
    return visitDate.isValid() ? visitDate.format('YYYY-MM-DD') : '';
  }, [isCopiedVisit, visit?.visit_datetime_from, visitDate]);
  const { data: copiedPatientResult } = useQuery({
    queryKey: [
      'visitSchedule',
      'copiedPatient',
      tenant,
      clinicNumber,
      visit?.patient_id,
      copiedVisitDateStr,
      serviceType,
    ],
    queryFn: () => fetchPatientEdit(tenant, clinicNumber, String(visit!.patient_id)),
    enabled:
      isOpen &&
      isCopiedVisit &&
      !!tenant &&
      !!clinicNumber &&
      !!visit?.patient_id &&
      !!copiedVisitDateStr,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isOpen || !isCopiedVisit || !visit?.patient_id || !copiedPatientResult) {
      return;
    }
    setPatientList([copiedPatientResult]);
    setPatientId(copiedPatientResult.id);
  }, [copiedPatientResult, isCopiedVisit, isOpen, visit?.patient_id]);

  // When search returns results: fill patient list + select first patient, keep 受診者名 field as-is.
  useEffect(() => {
    if (!searchResult || searchTrigger === 0) return;
    if (searchResult.data && searchResult.data.length > 0) {
      const list = searchResult.data;
      setPatientList(list);
      const defaultPatient = pickSearchedPatient(list, searchedKeyword);
      hasChangedPatientRef.current = true;
      setPatientId(defaultPatient.id);
      setPatientAddressName('');
      setSearchNoResult(false);
    } else {
      error({ title: NO_PRINT_DATA_MESSAGE, okText: 'OK' });
      setPatientList([]);
      setPatientId(undefined);
      setPatientAddressName('');
      setIsSuddenlyVisit(false);
      setSearchNoResult(true);
    }
  }, [searchResult?.data]);

  // Reset entire form when modal closes (don't hardcode defaults here, use constants/initial values)
  useEffect(() => {
    if (!isOpen) {
      hasChangedPatientRef.current = false;
      setPatientSearchKeyword('');
      setSearchedKeyword('');
      setSearchTrigger(0);
      setSearchVisitDate('');
      setPatientList([]);
      setPatientId(undefined);
      setPatientAddressName('');
      setVisitDate(dayjs());
      setVisitHour(String(DEFAULT_VISIT_HOUR).padStart(2, '0'));
      setVisitMinute('00');
      setVisitMinutes(DEFAULT_VISIT_MINUTES);
      setConsultationType(CONSULTATION_TYPE);
      setCopyTargetWeekdays([]);
      setIsElectric(true);
      setTreatmentMemo('');
      setIsSuddenlyVisit(false);
      setHospitalName('');
      setDoctorName('');
      setVisitReason('');
      setAlignmentDate('');
      setInitialFormData(null);
      setSearchNoResult(false);
      return;
    }
    const from = visit?.visit_datetime_from
      ? dayjs(visit.visit_datetime_from)
      : initialDate
        ? (() => {
            const d = dayjs(initialDate);
            if (initialTime) {
              const [h, m] = initialTime.split(':').map(Number);
              return d
                .hour(Number.isFinite(h) ? h : DEFAULT_VISIT_HOUR)
                .minute(Number.isFinite(m) ? m : 0);
            }
            return d.set('hour', DEFAULT_VISIT_HOUR).set('minute', 0);
          })()
        : dayjs();
    setVisitDate(from);
    setVisitHour(from.isValid() ? from.format('HH') : '');
    setVisitMinute(from.isValid() ? from.format('mm') : '');
    setVisitMinutes(visit?.visit_minutes ?? DEFAULT_VISIT_MINUTES);
    hasChangedPatientRef.current = false;
    setPatientId(visit?.patient_id ?? undefined);
    setPatientAddressName(visit?.patient_address_name ?? '');
    hasNormalizedSavedAddressRef.current = false;
    setTreatmentMemo(visit?.massager_remarks ?? '');
    setConsultationType(CONSULTATION_TYPE);
    setCopyTargetWeekdays([]);
    const initialIsElectric =
      serviceType === SERVICE_TYPE_R2
        ? visit?.is_not_hot_treatment === false
        : !(visit?.electric_treatment === 0);
    setIsElectric(initialIsElectric);
    setIsSuddenlyVisit(!!visit?.is_suddenly_visit);
    setHospitalName(visit?.hospital_name ?? '');
    setDoctorName(visit?.doctor_name ?? '');
    setVisitReason(visit?.visit_reason ?? '');
    setAlignmentDate(visit?.alignment_date ?? '');
    setInitialFormData({
      visitDate: from.format('YYYY-MM-DD'),
      visitTime: from.isValid() ? from.format('HH:mm') : '',
      patientSearchKeyword: '',
      patientId: visit?.patient_id ?? undefined,
      patientAddressName: visit?.patient_address_name ?? '',
      visitMinutes: visit?.visit_minutes ?? DEFAULT_VISIT_MINUTES,
      consultationType: CONSULTATION_TYPE,
      isElectric: initialIsElectric,
      treatmentMemo: visit?.massager_remarks ?? '',
      isSuddenlyVisit: !!visit?.is_suddenly_visit,
      hospitalName: visit?.hospital_name ?? '',
      doctorName: visit?.doctor_name ?? '',
      visitReason: visit?.visit_reason ?? '',
      alignmentDate: visit?.alignment_date ?? '',
    });
    if (visit?.patient_id != null) {
      const name = visit.patient_name ?? visit.patient_address_name ?? `#${visit.patient_id}`;
      const serialNum =
        visit.serial_number != null && visit.serial_number !== ''
          ? visit.serial_number
          : String(visit.patient_id);
      setPatientList([
        {
          id: visit.patient_id,
          serial_number: Number(serialNum) || visit.patient_id,
          current_patient_information: {
            name,
            address_name: visit.patient_base_address_name ?? '',
            address1: visit.patient_base_address1 ?? '',
            address2: visit.patient_base_address2 ?? '',
            address3: visit.patient_base_address3 ?? '',
          },
        } as PatientFormData,
      ]);
      setPatientSearchKeyword('');
    } else {
      setPatientList([]);
      setPatientSearchKeyword('');
    }
    setSearchTrigger(0);
    setSearchedKeyword('');
    setSearchVisitDate('');
    setSearchNoResult(false);
  }, [isOpen, visit, initialDate, initialTime, serviceType]);

  // Auto-focus 受診者名 field when modal opens (delay to allow AppModal transition to complete
  // so antd Modal's internal focus management doesn't steal focus back).
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => patientSearchInputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  const selectedPatient = useMemo(
    () => patientList.find(p => Number(p.id) === Number(patientId)),
    [patientList, patientId]
  );

  const patientSelectOptions = useMemo(
    () =>
      patientList.map(p => ({
        value: p.id,
        label: getPatientNameOnly(p),
      })),
    [patientList]
  );

  const currentFormData = useMemo(
    () => ({
      visitDate: visitDate.format('YYYY-MM-DD'),
      visitTime: visitTimeHHMM,
      patientSearchKeyword,
      patientId,
      patientAddressName,
      visitMinutes,
      consultationType,
      isElectric,
      treatmentMemo,
      isSuddenlyVisit,
      hospitalName,
      doctorName,
      visitReason,
      alignmentDate,
    }),
    [
      visitDate,
      visitTimeHHMM,
      patientSearchKeyword,
      patientId,
      patientAddressName,
      visitMinutes,
      consultationType,
      isElectric,
      treatmentMemo,
      isSuddenlyVisit,
      hospitalName,
      doctorName,
      visitReason,
      alignmentDate,
    ]
  );

  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(
    currentFormData,
    initialFormData
  );
  const handleClose = useCallback(() => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  }, [confirmIfChanged, clearFormChange, onClose]);

  const handleSearchClick = useCallback(() => {
    const converted = mbConvertKana(patientSearchKeyword, 'CK');
    if (converted !== patientSearchKeyword) {
      setPatientSearchKeyword(converted);
    }
    const keyword = converted.trim();

    if (!keyword) {
      error({ title: '受診者名を入力して検索してください', okText: 'OK' });
      return;
    }

    if (!visitDate.isValid() || !isVisitTimeValid) {
      error({ title: '日付を正しく入力してください。', okText: 'OK' });
      return;
    }

    setSearchVisitDate(visitDate.format('YYYY-MM-DD'));
    setSearchedKeyword(keyword);
    setSearchTrigger(t => t + 1);
  }, [patientSearchKeyword, visitDate, isVisitTimeValid, error]);

  /** Active patient_informations row at visitDate (per BD: filter available_to >= visitDate, sort ASC, take last). */
  const baseInfoAtVisitDate = useMemo(() => {
    if (!selectedPatient) return undefined;
    const visitDateStr = visitDate.format('YYYY-MM-DD');
    return (
      pickActivePatientInformation(
        selectedPatient.patient_informations as unknown as Array<
          { available_to?: string | null } & Record<string, unknown>
        >,
        visitDateStr
      ) ?? (selectedPatient.current_patient_information as unknown as Record<string, unknown>)
    );
  }, [selectedPatient, visitDate]);

  const visitBaseAddressLabel = useMemo(() => {
    return getAddressLabel({
      address_name: visit?.patient_base_address_name ?? '',
      address1: visit?.patient_base_address1 ?? '',
      address2: visit?.patient_base_address2 ?? '',
      address3: visit?.patient_base_address3 ?? '',
    });
  }, [
    visit?.patient_base_address_name,
    visit?.patient_base_address1,
    visit?.patient_base_address2,
    visit?.patient_base_address3,
  ]);

  // BD: default = patient_addresses.is_default=1; fallback = patient_informations base at visitDate
  useEffect(() => {
    if (!patientId) return;
    // Edit mode: keep the visit's saved address until the user actively re-picks a 受診者.
    if (!hasChangedPatientRef.current && visit?.patient_address_name != null) {
      if (!hasNormalizedSavedAddressRef.current) {
        const baseAddr =
          getAddressLabel(
            baseInfoAtVisitDate as
              | { address_name?: string; address1?: string; address2?: string; address3?: string }
              | undefined
          ) || visitBaseAddressLabel;

        // Legacy (R2/A2 visits/edit.blade.php:85) selects the saved row's address by
        // patient_address_id — 0 = 受診者情報, else that 他住所. Rows saved by legacy hold
        // only the 他住所's name, which no option label matches.
        const savedAddressId = visit?.patient_address_id;
        if (!isCopiedVisit && typeof savedAddressId === 'number') {
          const savedAddress =
            savedAddressId > 0
              ? addressList.find(addr => Number(addr.id) === savedAddressId)
              : undefined;
          const savedLabel =
            savedAddressId === 0
              ? baseAddr
              : savedAddress
                ? formatPatientAddressLabel(savedAddress)
                : '';
          if (savedLabel) {
            hasNormalizedSavedAddressRef.current = true;
            if (savedLabel !== patientAddressName) {
              setPatientAddressName(savedLabel);
              setInitialFormData(prev =>
                prev ? { ...prev, patientAddressName: savedLabel } : prev
              );
            }
            return;
          }
          // The 他住所 list is still loading — wait for it rather than guess by name.
          if (savedAddressId > 0 && addressResult === undefined) {
            return;
          }
        }
        const optionLabels = [
          baseAddr,
          ...[...addressList]
            .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
            .map(addr => formatPatientAddressLabel(addr)),
        ].filter((label): label is string => Boolean(label));

        if (optionLabels.length === 0) {
          return;
        }

        const normalizedAddressName = normalizeAddressLabelToOptions(
          visit.patient_address_name,
          optionLabels
        );
        if (normalizedAddressName) {
          hasNormalizedSavedAddressRef.current = true;
        }
        if (normalizedAddressName && normalizedAddressName !== patientAddressName) {
          setPatientAddressName(normalizedAddressName);
          setInitialFormData(prev =>
            prev ? { ...prev, patientAddressName: normalizedAddressName } : prev
          );
        }
      }
      return;
    }
    const baseAddr =
      getAddressLabel(
        baseInfoAtVisitDate as
          | { address_name?: string; address1?: string; address2?: string; address3?: string }
          | undefined
      ) || visitBaseAddressLabel;
    if (!isCopiedVisit && (!addressSearchVisitDate || addressResult === undefined)) {
      return;
    }
    const initialAddressLabel = resolveInitialPatientAddressLabel({
      addressList,
      baseAddressLabel: baseAddr,
      preferBaseAddress: isCopiedVisit,
    });
    if (initialAddressLabel) {
      setPatientAddressName(initialAddressLabel);
      if (isCopiedVisit) {
        setInitialFormData(prev =>
          prev ? { ...prev, patientAddressName: initialAddressLabel } : prev
        );
      }
    }
  }, [
    addressList,
    addressResult,
    addressSearchVisitDate,
    patientId,
    baseInfoAtVisitDate,
    visitBaseAddressLabel,
    visit?.patient_address_name,
    visit?.patient_address_id,
    isCopiedVisit,
  ]);

  // Change 受診者 (dropdown): wait for patient-addresses before choosing default/fallback address.
  const handlePatientSelectChange = useCallback(
    (value: number | undefined) => {
      hasChangedPatientRef.current = true;
      setPatientId(value);
      setPatientAddressName('');
    },
    []
  );

  useEffect(() => {
    const currentWeekday = visitDate.day();
    setCopyTargetWeekdays(prev => prev.filter(x => x !== currentWeekday));
  }, [visitDate]);

  /** RA2 No82: insurance_type_id=4 → 強制保険診療 */
  useEffect(() => {
    if (
      selectedPatient &&
      (selectedPatient.insurance_type_id === 4 || selectedPatient.insurance_type_id === '4')
    ) {
      setConsultationType(CONSULTATION_TYPE);
    }
  }, [selectedPatient?.insurance_type_id]);

  /**
   * Options for 受診者住所 (BD):
   *  row 1 = patient_informations base (always shown)
   *  row 2+ = patient_addresses sorted by id ASC
   */
  const addressSelectOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const baseAddr =
      getAddressLabel(
        baseInfoAtVisitDate as
          | { address_name?: string; address1?: string; address2?: string; address3?: string }
          | undefined
      ) || visitBaseAddressLabel;
    if (baseAddr) options.push({ value: baseAddr, label: baseAddr });
    [...addressList]
      .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
      .forEach(addr => {
        const label = formatPatientAddressLabel(addr);
        if (label && !options.some(o => o.value === label)) options.push({ value: label, label });
      });
    const normalizedPatientAddressName = normalizeAddressLabelToOptions(
      patientAddressName,
      options.map(option => option.value)
    );
    if (
      !isCopiedVisit &&
      normalizedPatientAddressName &&
      !options.some(o => o.value === normalizedPatientAddressName)
    ) {
      options.push({ value: normalizedPatientAddressName, label: normalizedPatientAddressName });
    }
    return options;
  }, [addressList, baseInfoAtVisitDate, visitBaseAddressLabel, patientAddressName, isCopiedVisit]);

  /**
   * patient_address_id of each 受診者住所 option, as legacy's select values
   * (getPatientAdresses_visits): 受診者情報 = 0, 他住所 = its id. Built in the options' own
   * order, so a label shared by two rows maps to the one the select shows.
   */
  const addressIdByLabel = useMemo(() => {
    const ids = new Map<string, number>();
    const baseAddr =
      getAddressLabel(
        baseInfoAtVisitDate as
          | { address_name?: string; address1?: string; address2?: string; address3?: string }
          | undefined
      ) || visitBaseAddressLabel;
    if (baseAddr) ids.set(baseAddr, 0);
    [...addressList]
      .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
      .forEach(addr => {
        const label = formatPatientAddressLabel(addr);
        if (label && !ids.has(label) && Number(addr.id) > 0) ids.set(label, Number(addr.id));
      });
    return ids;
  }, [addressList, baseInfoAtVisitDate, visitBaseAddressLabel]);

  useEffect(() => {
    const normalizedPatientAddressName = normalizeAddressLabelToOptions(
      patientAddressName,
      addressSelectOptions.map(option => option.value)
    );
    if (normalizedPatientAddressName && normalizedPatientAddressName !== patientAddressName) {
      setPatientAddressName(normalizedPatientAddressName);
    }
  }, [addressSelectOptions, patientAddressName]);

  const showDistanceWarning = Boolean(
    visit?.error_messages && String(visit.error_messages).includes(DISTANCE_WARNING_MSG)
  );

  const isR2 = serviceType === SERVICE_TYPE_R2;

  /** A2: 電療 — uses electric_treatment_is; R2: 温罨法・電療 — uses hot_treatment_is */
  const showNoElectricCheckboxResolved = useMemo(() => {
    // 表示条件 is driven by 同意書 alone — never by "new row vs existing row"
    // (legacy A2 visits/edit.blade.php:946, R2 visits/edit.blade.php:798 do the same).
    // No 電療 setting on the consent covering the visit date → hide.
    // Fresh 検索 result → backend attached the flag it derived from 同意書 at that date.
    if (selectedPatient) {
      const v = isR2
        ? (selectedPatient as { hot_treatment_is?: number | string | null }).hot_treatment_is
        : selectedPatient.electric_treatment_is;
      if (v != null) {
        return v !== 0 && v !== '0';
      }
    }
    // Edit/draft: 受診者 row is rebuilt from the visit (no 検索), so the stub carries no
    // *_treatment_is. Fall back to the 表示条件 computed per visit — by the backend for
    // saved visits, carried on the payload for drafts not yet データ更新'd.
    if (visit) return !!visit.patient_electric_treatment_eligible;
    return false;
  }, [selectedPatient, isR2, visit]);

  useEffect(() => {
    // Default from the patient master only when adding a new row; reopening a visit
    // (draft included) keeps the value already chosen on that visit.
    if (!isOpen || visit || !selectedPatient) return;
    if (isR2) {
      const v = (selectedPatient as { hot_treatment_is?: number | string | null }).hot_treatment_is;
      setIsElectric(v !== 0 && v !== '0');
    } else {
      const v = selectedPatient.electric_treatment_is;
      setIsElectric(v !== 0 && v !== '0');
    }
  }, [isOpen, visit, selectedPatient, isR2]);

  /** 突発的な往療 (RA2): shown when visit has is_suddenly_visit or patient has suddenly_visit_is (API) */
  const showSuddenlyVisitArea = useMemo(() => {
    // Hide when a just-run search returned no results — force user to re-search a valid patient.
    if (searchNoResult) return false;
    // Existing visit already saved as 突発的な往療 — always show so user can view/edit saved data
    // (regardless of locked state or whether patient's current conditions still qualify).
    if (visit?.is_suddenly_visit) return true;
    // BD 1: 訪問日 >= 2024-10-01
    if (visitDate.format('YYYY-MM-DD') < '2024-10-01') return false;
    if (!selectedPatient) return false;
    // BD 2 + 3a: consent covers visit date AND walking_condition_id=2
    // Fresh search result → backend attaches suddenly_visit_is on the patient.
    const v = selectedPatient.suddenly_visit_is;
    if (v != null) return v !== 0 && v !== '0';
    // Edit mode: selectedPatient is reconstructed from visit (no suddenly_visit_is).
    // Fall back to the eligibility flag computed per-visit by the backend.
    return !!visit?.patient_suddenly_visit_eligible;
  }, [
    selectedPatient,
    visit?.is_suddenly_visit,
    visit?.patient_suddenly_visit_eligible,
    visitDate,
    searchNoResult,
  ]);

  /** Compute list of dates (YYYY-MM-DD) to copy from copyTargetWeekdays, same logic as handleCopy (excludes original date). */
  const copyTargetDates = useMemo(() => {
    const baseWeekday = visitDate.day();
    const dates: string[] = [];
    copyTargetWeekdays.forEach(weekday => {
      let plusday = 0;
      if (baseWeekday === 0) {
        plusday = 0 - (7 - weekday);
      } else {
        if (baseWeekday < weekday) {
          plusday = weekday - baseWeekday;
        } else {
          if (weekday === 0) {
            plusday = 7 - baseWeekday;
          } else {
            plusday = 0 - (baseWeekday - weekday);
          }
        }
      }
      if (plusday === 0) return;
      const targetFrom = visitDate
        .clone()
        .add(plusday, 'day')
        .hour(Number(visitHour) || 0)
        .minute(Number(visitMinute) || 0)
        .second(0)
        .millisecond(0);
      dates.push(targetFrom.format('YYYY-MM-DD'));
    });
    return dates;
  }, [visitDate, visitHour, visitMinute, copyTargetWeekdays]);

  const getCurrentPayload = useCallback((): VisitDraftPayload => {
    const from = visitDate
      .clone()
      .hour(Number(visitHour) || 0)
      .minute(Number(visitMinute) || 0)
      .second(0)
      .millisecond(0);
    const safeMinutes = visitMinutes ?? 0;
    const to = from.add(safeMinutes, 'minute');
    const effectiveIsElectric = showNoElectricCheckboxResolved ? isElectric : false;
    const baseAddressFields = getPatientBaseAddressFields(
      baseInfoAtVisitDate as
        | { address_name?: unknown; address1?: unknown; address2?: unknown; address3?: unknown }
        | undefined,
      {
        patient_base_address1: visit?.patient_base_address1,
        patient_base_address2: visit?.patient_base_address2,
        patient_base_address3: visit?.patient_base_address3,
        patient_base_address_name: visit?.patient_base_address_name,
      }
    );
    // The saved row's own address, still shown as it was saved because it matches no
    // option, is unchanged — so is its id.
    const selectedAddressId =
      addressIdByLabel.get(patientAddressName) ??
      (!hasChangedPatientRef.current &&
      !isCopiedVisit &&
      patientAddressName === (visit?.patient_address_name ?? '')
        ? visit?.patient_address_id
        : undefined);
    const payload: VisitDraftPayload = {
      patient_id: patientId,
      patient_address_name: patientAddressName || undefined,
      patient_address_id: selectedAddressId,
      ...baseAddressFields,
      patient_name: selectedPatient?.current_patient_information?.name,
      serial_number:
        selectedPatient?.serial_number != null ? String(selectedPatient.serial_number) : undefined,
      visit_datetime_from: from.format('YYYY-MM-DDTHH:mm:ss'),
      visit_datetime_to: to.format('YYYY-MM-DDTHH:mm:ss'),
      visit_minutes: safeMinutes,
      massager_remarks: treatmentMemo || undefined,
      ...(isR2
        ? { is_not_hot_treatment: !effectiveIsElectric }
        : { electric_treatment: effectiveIsElectric ? 1 : 0 }),
      is_insurance: consultationType !== CONSULTATION_TYPE,
      is_suddenly_visit: isSuddenlyVisit,
      hospital_name: isSuddenlyVisit ? hospitalName || undefined : undefined,
      doctor_name: isSuddenlyVisit ? doctorName || undefined : undefined,
      visit_reason: isSuddenlyVisit ? visitReason || undefined : undefined,
      alignment_date: isSuddenlyVisit ? alignmentDate || undefined : undefined,
      patient_suddenly_visit_eligible: showSuddenlyVisitArea,
      // Drafts have no 検索 result to re-derive 同意書 from on reopen — carry 表示条件 along.
      patient_electric_treatment_eligible: showNoElectricCheckboxResolved,
      // UAT: carry source visit's error state (red bg + message) onto the copied row
      // so the copy keeps the error until the next データ更新 recomputes it.
      is_error: visit?.is_error,
      error_messages: visit?.error_messages,
    };
    if (copyTargetDates.length > 0) {
      payload.copy_target_dates = copyTargetDates;
    }
    return payload;
  }, [
    visitDate,
    visitHour,
    visitMinute,
    visitMinutes,
    patientId,
    patientAddressName,
    selectedPatient,
    treatmentMemo,
    isElectric,
    showNoElectricCheckboxResolved,
    consultationType,
    isSuddenlyVisit,
    // 令和8年法改正 No 5 — without these the memoised payload keeps the checkbox value from
    // the render it was built in, so ticking or unticking the box never reached 適用.
    hospitalName,
    doctorName,
    visitReason,
    alignmentDate,
    copyTargetDates,
    showSuddenlyVisitArea,
    baseInfoAtVisitDate,
    visit?.patient_base_address1,
    visit?.patient_base_address2,
    visit?.patient_base_address3,
    visit?.patient_base_address_name,
    addressIdByLabel,
    isCopiedVisit,
    visit?.patient_address_name,
    visit?.patient_address_id,
  ]);

  const {
    footerActionBusy,
    handleSave,
    handleDelete,
    handleCopy,
    isCopying,
    isApplying,
    isDeleting,
  } = useVisitScheduleModalActions({
    tenant,
    clinicNumber,
    serviceType,
    visit,
    isExisting,
    isSaving,
    patientId,
    visitDate,
    visitHour,
    visitMinute,
    isVisitTimeValid,
    visitMinutes,
    copyTargetDates,
    isSuddenlyVisit,
    hospitalName,
    doctorName,
    visitReason,
    alignmentDate,
    existingEvents,
    pendingDeletedVisitIds,
    currentMassagerId,
    massagers,
    getCurrentPayload,
    onSave,
    onDelete,
    onCopyToOtherDays,
    onClose,
    clearFormChange,
  });

  return (
    <AppModal
      title="訪問入力"
      isOpen={isOpen}
      onClose={handleClose}
      closable
      maxWidth="max-w-lg"
      footer={null}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          maxHeight: '70vh',
        },
      }}
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <VisitScheduleModalForm
          visit={visit}
          tenant={tenant}
          checkinEnabled={checkinEnabled}
          visitDate={visitDate}
          setVisitDate={setVisitDate}
          visitHour={visitHour}
          setVisitHour={setVisitHour}
          visitMinute={visitMinute}
          setVisitMinute={setVisitMinute}
          patientSearchInputRef={patientSearchInputRef}
          patientSearchKeyword={patientSearchKeyword}
          setPatientSearchKeyword={setPatientSearchKeyword}
          handleSearchClick={handleSearchClick}
          isSearching={isSearching}
          patientId={patientId}
          selectedPatient={selectedPatient}
          patientSelectOptions={patientSelectOptions}
          handlePatientSelectChange={handlePatientSelectChange}
          showSuddenlyVisitArea={showSuddenlyVisitArea}
          isSuddenlyVisit={isSuddenlyVisit}
          setIsSuddenlyVisit={setIsSuddenlyVisit}
          hospitalName={hospitalName}
          setHospitalName={setHospitalName}
          doctorName={doctorName}
          setDoctorName={setDoctorName}
          visitReason={visitReason}
          setVisitReason={setVisitReason}
          alignmentDate={alignmentDate}
          setAlignmentDate={setAlignmentDate}
          patientAddressName={patientAddressName}
          setPatientAddressName={setPatientAddressName}
          addressSelectOptions={addressSelectOptions}
          showDistanceWarning={showDistanceWarning}
          addressList={addressList}
          baseInfoAtVisitDate={baseInfoAtVisitDate}
          visitMinutes={visitMinutes}
          setVisitMinutes={setVisitMinutes}
          showNoElectricCheckboxResolved={showNoElectricCheckboxResolved}
          isElectric={isElectric}
          setIsElectric={setIsElectric}
          isR2={isR2}
          consultationType={consultationType}
          setConsultationType={setConsultationType}
          copyTargetWeekdays={copyTargetWeekdays}
          setCopyTargetWeekdays={setCopyTargetWeekdays}
          treatmentMemo={treatmentMemo}
          setTreatmentMemo={setTreatmentMemo}
        />

        {/* Footer (RA2): 削除 also applies to local draft visits (draft-* ids) while unlocked. */}
        <div className="flex flex-wrap justify-between flex-shrink-0 gap-2 px-4 py-4 bg-white border-t border-gray-200">
          <div className="flex gap-2">
            {canDeleteVisit && (
              <AppButton
                type="primary"
                danger
                onClick={handleDelete}
                loading={isDeleting}
                disabled={footerActionBusy}
                icon={<i className="fa fa-trash-o" />}
              >
                削除
              </AppButton>
            )}
          </div>
          <div className="flex gap-2">
            {isExisting && !isSuddenlyVisit && (
              <AppButton
                type="primary"
                onClick={handleCopy}
                loading={isCopying}
                disabled={footerActionBusy || copyTargetWeekdays.length > 0}
                icon={<i className="fa fa-copy" />}
              >
                コピー
              </AppButton>
            )}
            {!visit?.is_locked && (
              <AppButton
                type="primary"
                onClick={handleSave}
                loading={isSaving || isApplying}
                disabled={footerActionBusy}
                icon={<i className="mr-2 fa fa-paper-plane" />}
              >
                適用
              </AppButton>
            )}
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default VisitScheduleModal;
